import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './styles/main.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import toastConfigs from './toast.configs.tsx';
import { ThemeProvider } from '@emotion/react';
import MuiTheme from '@/mui.config.ts';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import * as Sentry from '@sentry/react';
import { Error as ErrorBoundaryComp } from './shared/ui';
import { CacheProvider } from '@emotion/react';
import { myCache } from './emotionCacheConf.ts';
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { CookiesProvider } from 'react-cookie';
import { setAxiosInterceptors } from './shared/services/http/interceptors.ts';
import { http as axiosInstance } from './shared/services/http';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { store } from '@/store';
import { setPosthogInitialized } from '@/store/slices/posthog';
import { isAdvertisingConsentGranted } from '@/shared/helpers/consent';


setAxiosInterceptors(axiosInstance);

// comment to test semantic release
const gtmParams = { id: import.meta.env.VITE_APP_GTM_ID || '' };
const posthogHost = import.meta.env.VITE_APP_POST_HOG_HOST || '';
const posthogUiHost = import.meta.env.VITE_APP_POST_HOG_UI_HOST || '';
const posthogKey = import.meta.env.VITE_APP_POST_HOG_ID || '';
const isProd = import.meta.env.VITE_APP_IS_PRODUCTION === 'true';

if (isProd) {
    Sentry.init({
        release: 'v1.0.0', // temporarily hardcoded
        dsn: 'https://5c951421715c126568000870f8a25f31@o4507772120072192.ingest.us.sentry.io/4507772189474816',
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            })
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: ["CanceledError"],
    });
}

// Advertising consent denied => skip init entirely; usePostHog()/window.posthog stay gated off.
if (isAdvertisingConsentGranted()) {
    console.log('Advertising consent granted, initializing PostHog');
    posthog.init(posthogKey, {
        api_host: posthogHost,
        ui_host: posthogUiHost,
        capture_pageview: false,
        disable_session_recording: true,
        persistence: 'sessionStorage' as 'memory' | 'localStorage' | 'cookie' | 'localStorage+cookie' | 'sessionStorage' | undefined,
        on_request_error: (error) => {
            if (!isProd) return

            // Create a proper Error instance for Sentry
            const sentryError = new Error(`PostHog request failed with status ${error.statusCode}`)

            // Attach full response for context
            Sentry.captureException(sentryError, {
                extra: {
                    statusCode: error.statusCode,
                    text: error.text,
                    json: error.json,
                },
                level: 'error', // optional: sets severity
            })
        }
    });

    store.dispatch(setPosthogInitialized(true));
    window.posthog = posthog;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <CookiesProvider>
            <PostHogProvider client={posthog}>
                <CacheProvider value={myCache}>
                    <GTMProvider state={gtmParams}>
                        <ToastContainer {...toastConfigs} />
                        <ThemeProvider theme={MuiTheme}>
                            <Sentry.ErrorBoundary onError={e => console.log(e)} fallback={<ErrorBoundaryComp />}>
                                <App />
                            </Sentry.ErrorBoundary>
                        </ThemeProvider>
                    </GTMProvider>
                </CacheProvider>
            </PostHogProvider>
        </CookiesProvider>
    </React.StrictMode>
);
